/**
 * A client's side of an online fight (docs/architecture.md §7): rollback prediction. Pure, so
 * tests can drive it with a fake network.
 *
 * Two timelines:
 * - **confirmed**: the server's frames applied in order. Always identical to the server's state
 *   at the same tick; the server's periodic hash checks it.
 * - **predicted**: confirmed plus the ticks the server hasn't sent yet, simulated with the local
 *   player's own inputs and a guess for the opponent (a bot is computed exactly, as the server
 *   does; a human is assumed to keep their last known input). This is what the player sees.
 *
 * Whenever frames arrive the prediction is rebuilt from the confirmed state (a rollback). With
 * two fighters that is a few dozen cheap steps.
 */
import { botInput, type BotState } from '../bot/bot';
import { step } from '../sim/fight';
import { NO_INPUT, type FighterInput } from '../sim/input';
import { cloneFight, copyFightInto, hashFight } from '../sim/state';
import type { FightEvent, FightState, Side } from '../sim/types';
import { cloneBot, createMatchBot, createMatchFight, type OnlineMatch } from './match';
import type { Frame } from './protocol';

export type EventSink = (events: FightEvent[], state: FightState) => void;

export class Predictor {
  readonly local: Side;
  readonly remote: Side;
  /** The server's timeline, up to the last frame received. */
  confirmed: FightState;
  /** What the player sees: the predicted state at `tick`, and the one before it. */
  state: FightState;
  prev: FightState;
  /** The opponent's bot, advanced with the confirmed timeline (null against a human). */
  private confirmedBot: BotState | null;
  /** The opponent's bot along the predicted timeline (continues between rebuilds). */
  private predictedBot: BotState | null = null;
  /** The latest tick whose events went out (predicted or confirmed). */
  private emittedTick = 0;
  private readonly ownInputs = new Map<number, FighterInput>();
  /** The opponent's last confirmed input: the guess for ticks not confirmed yet. */
  private lastRemote: FighterInput = NO_INPUT;
  /** Frames confirmed since the last prediction rebuild. */
  private dirty = false;
  /** The confirmed bot input disagreed with the server's (a desync symptom); for diagnostics. */
  botMismatches = 0;

  constructor(match: OnlineMatch, local: Side) {
    this.local = local;
    this.remote = local === 0 ? 1 : 0;
    this.confirmed = createMatchFight(match);
    this.state = cloneFight(this.confirmed);
    this.prev = cloneFight(this.confirmed);
    // Only the opponent can be a bot (the local side is this player).
    this.confirmedBot = createMatchBot(match, this.remote);
  }

  get confirmedTick(): number {
    return this.confirmed.tick;
  }

  get tick(): number {
    return this.state.tick;
  }

  /** How far the prediction runs ahead of the server's frames. */
  get ahead(): number {
    return this.state.tick - this.confirmed.tick;
  }

  /** The local player's input for tick `t` (already delayed and wire-quantized by the caller). */
  setOwnInput(t: number, input: FighterInput): void {
    this.ownInputs.set(t, input);
  }

  ownInput(t: number): FighterInput {
    return this.ownInputs.get(t) ?? NO_INPUT;
  }

  /**
   * Applies the server's frames. Frames for ticks already confirmed are skipped; a gap (a frame
   * after a missing one) stops there, which can't happen over an ordered transport. Ticks the
   * prediction never reached (a stall, a resync) send their events to `sink` from here.
   */
  confirm(firstTick: number, frames: readonly Frame[], sink?: EventSink): void {
    for (let k = 0; k < frames.length; k++) {
      const t = firstTick + k;
      if (t <= this.confirmed.tick) continue;
      if (t !== this.confirmed.tick + 1) break;
      const frame = frames[k]!;
      if (this.confirmedBot) {
        // Keep the bot's own state (its random numbers, what it has seen) in step with the server.
        const guess = botInput(this.confirmedBot, this.confirmed);
        const real = frame[this.remote];
        if (Math.abs(guess.mx - real.mx) > 0.01 || Math.abs(guess.my - real.my) > 0.01 || (guess.buttons & 15) !== real.buttons) {
          this.botMismatches++;
        }
      }
      const events = step(this.confirmed, frame);
      if (t > this.emittedTick) {
        this.emittedTick = t;
        if (sink && events.length) sink(events, this.confirmed);
      }
      this.lastRemote = frame[this.remote];
      this.dirty = true;
    }
    for (const t of this.ownInputs.keys()) if (t <= this.confirmed.tick) this.ownInputs.delete(t);
  }

  /**
   * Advances the prediction to tick `target` (never behind the confirmed state). If frames came
   * in since last time, it first rebuilds from the confirmed state. `sink` receives each
   * simulated tick's events (re-simulated ticks repeat theirs: event ids make them idempotent).
   */
  advanceTo(target: number, sink?: EventSink): void {
    const goal = Math.max(target, this.confirmed.tick);
    let bot: BotState | null;
    if (this.dirty || this.state.tick < this.confirmed.tick) {
      copyFightInto(this.state, this.confirmed);
      copyFightInto(this.prev, this.confirmed);
      bot = this.confirmedBot ? cloneBot(this.confirmedBot) : null;
      this.predictedBot = bot;
      this.dirty = false;
    } else {
      bot = this.predictedBot;
    }
    while (this.state.tick < goal) {
      const t = this.state.tick + 1;
      const frame: [FighterInput, FighterInput] = [NO_INPUT, NO_INPUT];
      frame[this.local] = this.ownInput(t);
      frame[this.remote] = bot ? botInput(bot, this.state) : this.lastRemote;
      copyFightInto(this.prev, this.state);
      const events = step(this.state, frame);
      this.emittedTick = Math.max(this.emittedTick, t);
      if (sink && events.length) sink(events, this.state);
    }
  }

  /** The confirmed state's hash, if the confirmed timeline is at `tick` (server hash check). */
  confirmedHashAt(tick: number): number | null {
    return this.confirmed.tick === tick ? hashFight(this.confirmed) : null;
  }

  /** Replaces the confirmed timeline with the server's (after a desync or a reconnect). */
  resync(state: FightState, bot: BotState | null, lastRemote: FighterInput): void {
    this.confirmed = state;
    this.confirmedBot = bot;
    this.lastRemote = lastRemote;
    for (const t of this.ownInputs.keys()) if (t <= state.tick) this.ownInputs.delete(t);
    this.state = cloneFight(state);
    this.prev = cloneFight(state);
    this.dirty = true;
  }
}
